import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { Toggle } from './Toggle.js';

// Event the footer "cookies" link and the Cookie Policy page dispatch to
// re-open the consent panel. Going through an event (instead of clearing
// localStorage + reloading) means it also works on localhost, where the
// banner is otherwise suppressed, and avoids a full page reload.
const OPEN_EVENT = 'pb:open-consent';

export function openConsentSettings() {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

// Extend Window so TypeScript knows gtag may exist. The real gtag is loaded
// by the inline script in index.html; on localhost it is never initialised.
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const STORAGE_KEY = 'pb_consent';
const STORAGE_DETAIL_KEY = 'pb_consent_detail';

type ConsentDetail = { analytics: boolean; ads: boolean };

function isLocalhost() {
  return location.hostname === 'localhost' || location.hostname === '127.0.0.1';
}

function updateGtag(detail: ConsentDetail) {
  window.gtag?.('consent', 'update', {
    analytics_storage: detail.analytics ? 'granted' : 'denied',
    ad_storage: detail.ads ? 'granted' : 'denied',
    ad_user_data: detail.ads ? 'granted' : 'denied',
    ad_personalization: detail.ads ? 'granted' : 'denied',
  });
}

export function ConsentBanner() {
  const [visible, setVisible] = useState<boolean>(() => {
    if (isLocalhost()) return false;
    return localStorage.getItem(STORAGE_KEY) === null;
  });

  const [customize, setCustomize] = useState(false);
  const [analyticsOn, setAnalyticsOn] = useState(true);
  const [adsOn, setAdsOn] = useState(false);

  // Re-open the panel on request (footer "cookies" link / Cookie Policy page).
  // Loads the saved preferences into the toggles and forces the panel visible
  // even on localhost, so users can always review and change their choice.
  useEffect(() => {
    const onOpen = () => {
      try {
        const raw = localStorage.getItem(STORAGE_DETAIL_KEY);
        if (raw) {
          const d = JSON.parse(raw) as ConsentDetail;
          setAnalyticsOn(Boolean(d.analytics));
          setAdsOn(Boolean(d.ads));
        }
      } catch {
        // ignore malformed stored detail
      }
      setCustomize(true);
      setVisible(true);
    };
    window.addEventListener(OPEN_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_EVENT, onOpen);
  }, []);

  if (!visible) return null;

  function acceptAll() {
    localStorage.setItem(STORAGE_KEY, 'granted');
    localStorage.setItem(
      STORAGE_DETAIL_KEY,
      JSON.stringify({ analytics: true, ads: true } satisfies ConsentDetail),
    );
    updateGtag({ analytics: true, ads: true });
    setVisible(false);
  }

  function rejectAll() {
    localStorage.setItem(STORAGE_KEY, 'denied');
    setVisible(false);
  }

  function savePreferences() {
    const detail: ConsentDetail = { analytics: analyticsOn, ads: adsOn };
    const top = analyticsOn || adsOn ? 'granted' : 'denied';
    localStorage.setItem(STORAGE_KEY, top);
    localStorage.setItem(STORAGE_DETAIL_KEY, JSON.stringify(detail));
    updateGtag(detail);
    setVisible(false);
  }

  return (
    // Fixed bottom strip on mobile, bottom-right card on md+. Sits above
    // page content (z-50) but does not block interaction elsewhere.
    <div className="fixed bottom-0 left-0 right-0 z-50 p-3 md:bottom-4 md:left-auto md:right-4 md:max-w-sm">
      <div className="rounded-lg border border-border bg-background shadow-lg">
        <div className="p-4">
          <p className="font-mono text-[12px] font-semibold uppercase tracking-wider text-foreground">
            We use cookies to improve Producer Battle.
          </p>
          <p className="mt-1.5 font-mono text-[11px] leading-relaxed text-muted-foreground">
            We use analytics cookies to understand how you use the site. Essential cookies for
            sign-in and security are always on. See our{' '}
            <Link to="/legal" hash="cookies" className="underline hover:text-foreground">
              cookie policy
            </Link>
            .
          </p>

          {customize && (
            <div className="mt-3 space-y-2 border-t border-border pt-3">
              <Toggle
                id="consent-analytics"
                checked={analyticsOn}
                onChange={setAnalyticsOn}
                label="Analytics"
                description="helps us understand which features get used."
              />
              <Toggle
                id="consent-ads"
                checked={adsOn}
                onChange={setAdsOn}
                label="Ads & marketing"
                description="currently unused; reserved for future campaigns."
              />
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            {customize ? (
              <button
                type="button"
                onClick={savePreferences}
                className="rounded-md bg-primary px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Save preferences
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={acceptAll}
                  className="rounded-md bg-primary px-3 py-1.5 font-mono text-[11px] font-bold uppercase tracking-wider text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  Accept all
                </button>
                <button
                  type="button"
                  onClick={rejectAll}
                  className="rounded-md border border-border px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition-colors hover:border-foreground/40 hover:text-foreground"
                >
                  Reject all
                </button>
                <button
                  type="button"
                  onClick={() => setCustomize(true)}
                  className="rounded-md px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-muted-foreground transition-colors hover:text-foreground"
                >
                  Customize
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
